import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Context
import { LanguageProvider } from "@/contexts/LanguageContext";

// Pages (lazy-loaded for code splitting)
const Home = React.lazy(() => import("@/pages/Home"));
const About = React.lazy(() => import("@/pages/About"));
const Contact = React.lazy(() => import("@/pages/Contact"));
const Blog = React.lazy(() => import("@/pages/Blog"));
const Article = React.lazy(() => import("@/pages/Article"));
const Category = React.lazy(() => import("@/pages/Category"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("@/pages/TermsOfService"));
const AffiliateDisclosure = React.lazy(() => import("@/pages/AffiliateDisclosure"));
const CookiePolicy = React.lazy(() => import("@/pages/CookiePolicy"));
const Services = React.lazy(() => import("@/pages/Services"));
const SetupAudit = React.lazy(() => import("@/pages/SetupAudit"));
const Quiz = React.lazy(() => import("@/pages/Quiz"));
const MediaKit = React.lazy(() => import("@/pages/MediaKit"));
const NotFound = React.lazy(() => import("@/pages/NotFound"));

// Layout
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

// Language-aware route wrapper
const LanguageRoutes = () => (
  <Routes>
    {/* Default English routes */}
    <Route path="/" element={<Home />} />
    <Route path="/about" element={<About />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:slug" element={<Article />} />
    <Route path="/category/:slug" element={<Category />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<TermsOfService />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/cookie-policy" element={<CookiePolicy />} />
    <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
    <Route path="/services" element={<Services />} />
    <Route path="/services/setup-audit" element={<SetupAudit />} />
    <Route path="/quiz" element={<Quiz />} />
    <Route path="/media-kit" element={<MediaKit />} />

    {/* Language-prefixed routes (ES, IT, DE, FR) */}
    <Route path="/:lang" element={<Home />} />
    <Route path="/:lang/about" element={<About />} />
    <Route path="/:lang/contact" element={<Contact />} />
    <Route path="/:lang/blog" element={<Blog />} />
    <Route path="/:lang/blog/:slug" element={<Article />} />
    <Route path="/:lang/category/:slug" element={<Category />} />
    <Route path="/:lang/privacy" element={<PrivacyPolicy />} />
    <Route path="/:lang/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/:lang/terms" element={<TermsOfService />} />
    <Route path="/:lang/terms-of-service" element={<TermsOfService />} />
    <Route path="/:lang/cookie-policy" element={<CookiePolicy />} />
    <Route path="/:lang/affiliate-disclosure" element={<AffiliateDisclosure />} />
    <Route path="/:lang/services" element={<Services />} />
    <Route path="/:lang/services/setup-audit" element={<SetupAudit />} />
    <Route path="/:lang/quiz" element={<Quiz />} />
    <Route path="/:lang/media-kit" element={<MediaKit />} />

    {/* 404 page for unknown routes */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

function App() {
  return (
    <LanguageProvider>
      <div className="min-h-screen bg-[#F9F7F2]">
        <BrowserRouter>
          <Header />
          <main>
            <Suspense fallback={
              <div className="min-h-[60vh] flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-[#2A9D8F] border-t-transparent rounded-full animate-spin" />
              </div>
            }>
              <LanguageRoutes />
            </Suspense>
          </main>
          <Footer />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
